// @flow
import * as React from "react"
import { compose } from "react-apollo"
import { FormattedMessage, injectIntl } from "react-intl"
import { connect } from "react-redux"
import moment from "moment"
import { TweenMax, Cubic } from "gsap"
import { setModal } from "../../Modal"
import { Legend, AqiLevel, AqiDescription, levels } from "../Shared/AQILegend"
import { Metrics, Metric, GraphContainer } from "../Shared/AQIChart"
import HistoryContainer from "./HistoryContainer"
import Graph from "./Graph"
import { ForecastMetricAlternate } from "../CurrentAirQualityView"
import { withCurrentKioskData } from "../queries"

import {
  Container,
  Modal,
  Content,
  HistoryBody,
  Header,
  PrimaryHeading,
  SecondaryTitleHeading,
  PrimaryButton,
  ButtonGroup,
  Fade
} from "../Modal"

import type { CurrentKiosk } from "../queries"
import type { Match, RouterHistory } from "react-router-dom"
import type { SetModal } from "../../Modal"
import type { IntlShape } from "react-intl"

export type SampleData = Array<{ occurredAt: string, value: number }>

type Props = {
  match: Match,
  history: RouterHistory,
  setModal: SetModal,
  data: CurrentKiosk,
  intl: IntlShape
}

class HistoryModal extends React.Component<Props> {
  titles: Array<HTMLElement>
  body: ?HTMLElement
  fade: ?HTMLElement
  legendKeys: Array<HTMLElement>
  metrics: Array<HTMLElement>

  constructor(props) {
    super(props)
    this.body = null
    this.fade = null
    this.legendKeys = []
    this.metrics = []
    this.titles = []
  }

  componentDidMount = () => {
    TweenMax.set(this.body, { y: "-=50", opacity: 0 })
    TweenMax.set(this.fade, { opacity: 0 })
    TweenMax.to(this.body, 0.5, { ease: Cubic.easeOut, opacity: 1, y: 0 })
    TweenMax.to(this.fade, 0.5, { ease: Cubic.easeOut, opacity: 1 })
    TweenMax.set(this.legendKeys, { opacity: 0, y: "+=50" })
    TweenMax.set(this.titles, { opacity: 0, y: "+=25" })
    TweenMax.staggerTo(
      this.legendKeys,
      0.5,
      { ease: Cubic.easeOut, opacity: 1, y: 0, delay: 0.5 },
      0.25
    )
    TweenMax.set(this.metrics, { opacity: 0, x: "+=50" })
    TweenMax.staggerTo(
      this.metrics,
      0.5,
      { ease: Cubic.easeOut, opacity: 1, x: 0, delay: 0.75 },
      0.25
    )
    TweenMax.staggerTo(
      this.titles,
      0.35,
      { ease: Cubic.easeOut, opacity: 1, y: 0, delay: 0.25 },
      0.25
    )
  }

  handleClose = () => {
    this.dismiss()
  }

  dismiss = () => {
    const { history, setModal } = this.props
    setModal(false)
    TweenMax.to(this.body, 0.5, { ease: Cubic.easeOut, opacity: 0, y: "+=50" })
    TweenMax.to(this.fade, 0.5, {
      ease: Cubic.easeOut,
      opacity: 0,
      onComplete: () => {
        history.goBack()
      }
    })
  }

  getHistoricData = () => {
    const { data } = this.props
    const history =
      (!data.loading &&
        data.currentKiosk &&
        data.currentKiosk.readings &&
        data.currentKiosk.readings.history) ||
      []
    const historicData = [...history].sort(
      (a, b) => (moment(a.occurredAt).isBefore(b.occurredAt) ? -1 : 1)
    )
    return historicData
  }

  render() {
    console.log(this.props.data)
    const { intl: { formatMessage }, data } = this.props
    const currentKiosk = data.currentKiosk || {}
    return (
      <Container>
        <Fade
          onClick={this.handleClose}
          dark
          innerRef={el => (this.fade = el)}
        />
        <Modal>
          <HistoryBody innerRef={el => (this.body = el)}>
            <Header>
              <PrimaryHeading innerRef={el => this.titles.push(el)}>
                <FormattedMessage id="kiosk.history.title" />
              </PrimaryHeading>
              <SecondaryTitleHeading innerRef={el => this.titles.push(el)}>
                {formatMessage({ id: "kiosk.history.subtitle" }).replace(
                  "[SHORT NAME]",
                  data.loading ? "" : currentKiosk.shortName
                )}
              </SecondaryTitleHeading>
            </Header>
            <Content>
              <HistoryContainer>
                <Legend>
                  {levels.map(level => (
                    <AqiLevel
                      key={level}
                      level={level}
                      innerRef={el => this.legendKeys.push(el)}
                    >
                      <AqiDescription>
                        <FormattedMessage
                          id={`kiosk.history.${level
                            .toLowerCase()
                            .replace(" ", "")}`}
                        />
                      </AqiDescription>
                    </AqiLevel>
                  ))}
                </Legend>
                <Metrics>
                  {["pm2_5", "pm10", "o3"].map((metric, index) => (
                    <Metric
                      innerRef={el => this.metrics.push(el)}
                      key={`metric_${index}`}
                    >
                      <ForecastMetricAlternate pollutant={metric} />
                      <GraphContainer>
                        <Graph
                          values={this.getHistoricData().map(
                            ({ occurredAt, ...values }) => ({
                              occurredAt: moment(occurredAt).format("M/D"),
                              value: values[metric]
                            })
                          )}
                          delay={0.75 + index * 0.75}
                        />
                      </GraphContainer>
                    </Metric>
                  ))}
                </Metrics>
              </HistoryContainer>
              <ButtonGroup>
                <PrimaryButton
                  onClick={this.handleClose}
                  innerRef={el => this.titles.push(el)}
                >
                  <FormattedMessage id="kiosk.history.close" />
                </PrimaryButton>
              </ButtonGroup>
            </Content>
          </HistoryBody>
        </Modal>
      </Container>
    )
  }
}

const ModalWithData = compose(withCurrentKioskData)(HistoryModal)

export default connect(null, {
  setModal
})(injectIntl(ModalWithData))



// WEBPACK FOOTER //
// ./src/Kiosk/HistoryModal/HistoryModal.js